import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import type { OrdreStatus } from "types/ordre";
import { STATUS_CHIP_STYLES } from "./dropdown/OrdreStatusDropdown";
import { ordresApi } from "@services/api/ordres";
import { useOrdreStore } from "@store/ordreStore";
import { showNotification } from "context/notification.service";

const STATUS_GROUPS = [
  { label: "Préparation", statuses: ["a_preparer", "devis_initial", "commande", "pret"] },
  { label: "Travaux", statuses: ["reception", "en_cours", "devis_supp", "accord", "acompte"] },
  { label: "Clôture", statuses: ["fin_de_travaux", "facturation", "solde", "solde_paye", "disponible", "restitue"] },
];

const STATUS_OPTIONS: { value: OrdreStatus; label: string }[] = [
  { value: "a_preparer", label: "À préparer" },
  { value: "devis_initial", label: "Devis initial" },
  { value: "commande", label: "Commandé" },
  { value: "pret", label: "Prêt" },
  { value: "reception", label: "Réceptionné" },
  { value: "en_cours", label: "En cours" },
  { value: "devis_supp", label: "Devis supp." },
  { value: "accord", label: "Accord" },
  { value: "acompte", label: "Acompte" },
  { value: "fin_de_travaux", label: "Fin de travaux" },
  { value: "facturation", label: "Facturation" },
  { value: "solde", label: "Solde" },
  { value: "solde_paye", label: "Solde payé" },
  { value: "disponible", label: "Disponible" },
  { value: "restitue", label: "Restitué" },
];

export interface ContextMenuState {
  x: number;
  y: number;
  ordreId: string;
  currentStatus: OrdreStatus;
}

export function PlanningContextMenu({
  menu,
  onClose,
}: {
  menu: ContextMenuState;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [savingStatus, setSavingStatus] = useState<OrdreStatus | null>(null);

  // Position the menu so it doesn't overflow the viewport
  const [pos, setPos] = useState({ top: menu.y, left: menu.x });
  useEffect(() => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    let top = menu.y;
    let left = menu.x;
    if (top + rect.height > window.innerHeight - 8) {
      top = window.innerHeight - rect.height - 8;
    }
    if (left + rect.width > window.innerWidth - 8) {
      left = window.innerWidth - rect.width - 8;
    }
    setPos({ top: Math.max(8, top), left: Math.max(8, left) });
  }, [menu.x, menu.y]);

  // Close on outside click or Escape
  const handleClose = useCallback((e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) onClose();
  }, [onClose]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("mousedown", handleClose);
    document.addEventListener("keydown", handleKey);
    // Also close on scroll (any scrollable parent)
    const handleScroll = () => onClose();
    window.addEventListener("scroll", handleScroll, true);
    return () => {
      document.removeEventListener("mousedown", handleClose);
      document.removeEventListener("keydown", handleKey);
      window.removeEventListener("scroll", handleScroll, true);
    };
  }, [handleClose, onClose]);

  const currentIdx = STATUS_OPTIONS.findIndex((s) => s.value === menu.currentStatus);

  const handleStatusChange = async (newStatus: OrdreStatus) => {
    if (newStatus === menu.currentStatus || savingStatus) return;
    setSavingStatus(newStatus);
    try {
      const updated = await ordresApi.update(menu.ordreId, { status: newStatus });
      useOrdreStore.getState().updateOrdre(updated);
      onClose();
    } catch {
      showNotification({ type: "error", message: "Erreur lors du changement de statut" });
      setSavingStatus(null);
    }
  };

  return createPortal(
    <div
      ref={ref}
      className="bg-white rounded-xl shadow-lg border border-neutral-200"
      style={{
        position: "fixed",
        top: pos.top,
        left: pos.left,
        zIndex: 9999,
        minWidth: 180,
        paddingTop: 6,
        paddingBottom: 6,
        pointerEvents: savingStatus ? "none" : "auto",
      }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        style={{
          padding: "2px 12px 6px",
          fontFamily: "Poppins",
          fontSize: 11,
          fontWeight: 600,
          color: "#525252",
        }}
      >
        Changer le statut
      </div>
      {STATUS_GROUPS.map((group, gi) => (
        <div key={group.label}>
          {gi > 0 && <div style={{ height: 1, background: "#F0F0F0", margin: "3px 12px" }} />}
          <div
            style={{
              padding: "3px 12px 2px",
              fontFamily: "Poppins",
              fontSize: 10,
              fontWeight: 500,
              color: "#C0C0C0",
              textTransform: "uppercase",
              letterSpacing: 0.5,
            }}
          >
            {group.label}
          </div>
          {group.statuses.map((sv) => {
            const idx = STATUS_OPTIONS.findIndex((s) => s.value === sv);
            const isCurrent = sv === menu.currentStatus;
            const isSaving = savingStatus === sv;
            const isPast = idx < currentIdx;
            const chip = STATUS_CHIP_STYLES[sv] || { label: sv, bg: "#F4F4F4", color: "#A1A1A1" };
            return (
              <button
                key={sv}
                className={`flex flex-row items-center w-full text-left${isCurrent ? " cursor-default" : " hover:bg-neutral-50 transition-colors cursor-pointer"}`}
                style={{ padding: "3px 12px 3px 20px" }}
                onClick={isCurrent ? undefined : () => handleStatusChange(sv as OrdreStatus)}
                disabled={isCurrent || !!savingStatus}
              >
                {isSaving ? (
                  <svg width={10} height={10} viewBox="0 0 10 10" className="animate-spin" style={{ marginRight: 6, flexShrink: 0 }}>
                    <circle cx="5" cy="5" r="4" fill="none" stroke="#A1A1A1" strokeWidth={1.5} strokeDasharray="18" strokeDashoffset="6" strokeLinecap="round" />
                  </svg>
                ) : isCurrent ? (
                  <svg width={10} height={10} viewBox="0 0 10 10" fill="none" style={{ marginRight: 6, flexShrink: 0 }}>
                    <path d="M2 5l2.5 2.5L8 3" stroke="#1A1A1A" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : null}
                <span
                  className="whitespace-nowrap"
                  style={{
                    fontSize: 12,
                    fontWeight: isCurrent ? 600 : isSaving ? 600 : 500,
                    color: isSaving ? "#525252" : isCurrent ? "#1A1A1A" : isPast ? "#C0C0C0" : "#666666",
                    fontFamily: "Poppins",
                    marginLeft: isCurrent || isSaving ? 0 : 16,
                  }}
                >
                  {chip.label}
                </span>
              </button>
            );
          })}
        </div>
      ))}
    </div>,
    document.body,
  );
}
